// components/UndoRedoControls.tsx

import React, { useState } from 'react';
import {
  Group,
  ActionIcon,
  Tooltip,
  Modal,
  Timeline,
  Text,
  ScrollArea,
  Badge,
  Kbd,
  Button,
  Divider,
} from '@mantine/core';
import {
  IconArrowBackUp,
  IconArrowForwardUp,
  IconHistory,
  IconTrash,
  IconClock,
} from '@tabler/icons-react';
import { format } from 'date-fns';

interface HistoryItem {
  description: string;
  timestamp: number;
  isCurrent: boolean;
  index: number;
}

interface UndoRedoControlsProps {
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  getHistory: () => HistoryItem[];
  clearHistory: () => void;
  disabled?: boolean;
  undoLoading?: boolean;
  redoLoading?: boolean;
}

export const UndoRedoControls: React.FC<UndoRedoControlsProps> = ({
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  getHistory,
  clearHistory,
  disabled = false,
  undoLoading = false,
  redoLoading = false,
}) => {
  const [historyModalOpened, setHistoryModalOpened] = useState(false);
  const history = getHistory();
  
  // Keyboard shortcuts
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (disabled) return;
      
      // Ctrl/Cmd + Z for undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (canUndo) onUndo();
      }
      
      // Ctrl/Cmd + Shift + Z or Ctrl/Cmd + Y for redo
      if (
        ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'z') ||
        ((e.ctrlKey || e.metaKey) && e.key === 'y')
      ) {
        e.preventDefault();
        if (canRedo) onRedo();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canUndo, canRedo, onUndo, onRedo, disabled]);
  
  const getActionColor = (description: string) => {
    if (description.includes('Delete') || description.includes('Remove')) return 'red';
    if (description.includes('Assign') || description.includes('Add')) return 'green';
    if (description.includes('Bulk')) return 'blue';
    if (description.includes('Excel')) return 'violet';
    return 'gray';
  };
  
  return (
    <>
      <Group gap="xs">
        <Tooltip
          label={
            <Group gap="xs">
              <Text size="xs">Undo</Text>
              <Kbd size="xs">Ctrl+Z</Kbd>
            </Group>
          }
          withArrow
        >
          <ActionIcon
            variant="light"
            loading={undoLoading}
            disabled={!canUndo || disabled || undoLoading}
            onClick={onUndo}
            size="lg"
            aria-label="Undo"
          >
            <IconArrowBackUp size={16} />
          </ActionIcon>
        </Tooltip>
        
        <Tooltip
          label={
            <Group gap="xs">
              <Text size="xs">Redo</Text>
              <Kbd size="xs">Ctrl+Shift+Z</Kbd>
            </Group>
          }
          withArrow
        >
          <ActionIcon
            variant="light"
            loading={redoLoading}
            disabled={!canRedo || disabled || redoLoading}
            onClick={onRedo}
            size="lg"
            aria-label="Redo"
          >
            <IconArrowForwardUp size={16} />
          </ActionIcon>
        </Tooltip>
        
        <Tooltip label="View History" withArrow>
          <ActionIcon
            variant="light"
            onClick={() => setHistoryModalOpened(true)}
            disabled={disabled}
            size="lg"
            aria-label="View History"
          >
            <IconHistory size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>
      
      <Modal
        opened={historyModalOpened}
        onClose={() => setHistoryModalOpened(false)}
        title="Action History"
        size="md"
      >
        <Group justify="space-between" mb="md">
          <Text size="sm" c="dimmed">
            {history.length} action{history.length !== 1 ? 's' : ''} in history
          </Text>
          <Button
            variant="light"
            color="red"
            size="xs"
            leftSection={<IconTrash size={14} />}
            onClick={() => {
              clearHistory();
              setHistoryModalOpened(false);
            }}
            disabled={history.length < 1}
          >
            Clear History
          </Button>
        </Group>
        
        <Divider mb="md" />
        
        <ScrollArea h={400}>
          <Timeline active={history.findIndex(h => h.isCurrent)} bulletSize={24}>
            {history.map((item, index) => (
              <Timeline.Item
                key={index}
                bullet={
                  item.isCurrent ? (
                    <IconClock size={12} />
                  ) : (
                    <Text size="xs">{index}</Text>
                  )
                }
                title={
                  <Group gap="xs">
                    <Text
                      size="sm"
                      fw={item.isCurrent ? 700 : 400}
                    >
                      {item.description}
                    </Text>
                    {item.isCurrent && (
                      <Badge size="xs" color="blue">
                        Current
                      </Badge>
                    )}
                  </Group>
                }
              >
                <Text size="xs" c="dimmed">
                  {format(new Date(item.timestamp), 'HH:mm:ss')}
                </Text>
                <Badge
                  size="xs"
                  color={getActionColor(item.description)}
                  variant="light"
                  mt="xs"
                >
                  {item.description.split(' ')[0]}
                </Badge>
              </Timeline.Item>
            ))}
          </Timeline>
        </ScrollArea>
        
        <Divider mt="md" mb="sm" />
        
        <Text size="xs" c="dimmed">
          History shows all actions performed
        </Text>
      </Modal>
    </>
  );
};